import { capitalize } from 'lodash';
import { ReactNode } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  DeleteMenuItemWithConfirmationModal,
  DeleteMenuItemWithConfirmationModalProps,
} from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { useReportError } from '@/hooks/useReportError';
// eslint-disable-next-line luminary-rules/no-page-imports
import { CharitableTrustDesignerDataDocument } from '@/pages/designer/CharitableTrustDesigner/CharitableTrustDesignerBasicInformation/graphql/CharitableTrustDesignerData.generated';

import { ProposalKind } from '../../types/ProposalKind';
import { useDeleteProposalMutation } from './graphql/DeleteProposal.generated';

interface DeleteProposalMenuItemProps
  extends Partial<DeleteMenuItemWithConfirmationModalProps> {
  proposalId: string;
  onDelete?: () => void;
  kind?: ProposalKind;
}

export function DeleteProposalMenuItem({
  proposalId,
  onDelete,
  modalProps = {},
  menuItemProps = {},
  kind,
  label: externalLabel,
  ...deleteProps
}: DeleteProposalMenuItemProps) {
  const kindLabel = kind === ProposalKind.Entity ? 'proposal' : 'analysis';

  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [deleteProposal, { loading: deleting }] = useDeleteProposalMutation({
    variables: { proposalId },
    refetchQueries: 'active',
    onQueryUpdated: (query) => {
      return shouldRefetchQuery(query.queryName, {
        ignoredQueryDocuments: [CharitableTrustDesignerDataDocument],
      });
    },
    onError: (error) => {
      reportError(`failed to delete proposal`, error);
      showFeedback(
        `Failed to delete this ${kindLabel}. Please refresh the page and try again.`
      );
    },
    onCompleted: () => {
      showFeedback(`${capitalize(kindLabel)} deleted successfully`, {
        variant: 'success',
      });
      onDelete?.();
    },
  });

  let label: ReactNode = `Delete ${kindLabel}`;
  if (externalLabel) {
    label = externalLabel;
  }

  return (
    <DeleteMenuItemWithConfirmationModal
      label={label}
      onConfirmDelete={() => deleteProposal()}
      {...deleteProps}
      menuItemProps={{ loading: deleting, ...menuItemProps }}
      modalProps={{
        heading: `Are you sure you want to delete this ${kindLabel}?`,
        ...modalProps,
      }}
    />
  );
}
